import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse } from '../../../../types/types';
import { Event as PrismaEvent } from '@prisma/client';
import { ClientService } from './client/client.service';

export type ClientEvent = Partial<PrismaEvent>;

export interface PaginatedEventsResponse<T> {
  events: T;
  totalCount: number;
  page: number;
  pageSize: number;
}

@Injectable({
  providedIn: 'root',
})
export class EventService extends ClientService {
  private apiUrl = `${this.baseUrl}/api/events`; // Base URL for events API

  public getEvents(
    page: number = 1,
    pageSize: number = 10,
    startDate?: string, // Optional parameter for filtering events from a start date
    sortField: string = 'start', // Default sort field
    sortOrder: string = 'asc', // Default sort order
    onlyActive?: boolean
  ): Observable<ApiResponse<PaginatedEventsResponse<ClientEvent[]>>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString())
      .set('sortField', sortField)
      .set('sortOrder', sortOrder);

    // Conditionally add the 'startDate' query parameter if it is provided
    if (startDate) {
      params = params.set('startDate', startDate);
    }

    if (onlyActive !== undefined) {
      params = params.set('onlyActive', onlyActive.toString());
    }
    return this.get<ApiResponse<PaginatedEventsResponse<ClientEvent[]>>>(
      this.apiUrl,
      { params },
      {
        storageType: 'memory',
        ttlMS: 3600000, // 1 hour
      }
    );
  }

  public getEvent(eventId: number): Observable<ApiResponse<ClientEvent>> {
    // GET request to retrieve a single event by ID
    return this.get<ApiResponse<ClientEvent>>(
      `${this.apiUrl}/${eventId}`,
      undefined,
      {
        storageType: 'memory',
        ttlMS: 3600000, // 1 hour
      }
    );
  }

  public createEvent(event: ClientEvent): Observable<ApiResponse<ClientEvent>> {
    this.clearStorage();
    // POST request to create a new event
    return this.http.post<ApiResponse<ClientEvent>>(this.apiUrl, event);
  }

  public updateEvent(
    eventId: number,
    event: ClientEvent
  ): Observable<ApiResponse<ClientEvent>> {
    this.clearStorage();
    return this.http.put<ApiResponse<ClientEvent>>(
      `${this.apiUrl}/${eventId}`,
      event
    );
  }

  public deleteEvent(eventId: number): Observable<ApiResponse<null>> {
    this.clearStorage();
    return this.http.delete<ApiResponse<null>>(`${this.apiUrl}/${eventId}`);
  }
}
